import { useEffect, useState } from "react";
import { Costs } from "../../../interfaces/course-cost.interface";
import { Table, Typography } from "antd";
import "./registration-cost-table.scss";
import { CBSelect } from "../../shared";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { useQuery } from "@tanstack/react-query";
import { getRegisterByCourse } from "../../../services/course.service";
import { useParams } from "react-router-dom";
import GSTLicenses from "../../../interfaces/gst-licenses";
interface CostTableProps {
  error: string;
  editable?: boolean;
  onChange: (key: React.Key[], obj: Costs[]) => void;
  handleWaitList: (key: boolean, waitList: string) => void;
  handleFullCourse: (courseFull: boolean) => void;
  registrationSelected: string[];
  license: GSTLicenses | undefined;
  handleLicense: (license: string) => void;
}

const RegistrationCostTable = ({
  error,
  registrationSelected,
  onChange,
  license,
  handleLicense,
  handleWaitList,
  handleFullCourse,
}: CostTableProps) => {
  const { id } = useParams();
  const { Text } = Typography;
  const { control, watch } = useForm({
    mode: "onBlur",
    resolver: yupResolver(
      object({
        waitList: string().required(),
        license: string().required(),
      }),
    ),
    defaultValues: { waitList: "0", license: "0" },
  });

  const { data } = useQuery({
    queryKey: [`course-${id}-options`],
    queryFn: () => getRegisterByCourse(id),
  });

  const [costs, setCost] = useState<Costs[]>(data ?? []);
  const [selectedRegistration, setSelectedRegistration] =
    useState<string[]>(registrationSelected);
  const [waitList, setWaitList] = useState<boolean>(false);
  const [courseFull, setCourseFull] = useState<boolean>(false);
  const handleSelect = (e: Costs) => {
    if (e) {
      let optional: boolean = false;
      let fullCourse: boolean = false;
      setSelectedRegistration([e.key!]);
      if (
        Number(e.registered) + 1 > Number(e.maxOfStudents) &&
        String(e.enableWaitlist) === "1"
      )
        optional = true;
      else if (
        Number(e.registered) + 1 > Number(e.maxOfStudents) &&
        String(e.enableWaitlist) === "0"
      ) {
        fullCourse = true;
      } else {
        optional = false;
      }

      setWaitList(optional);
      handleWaitList(optional, watch("waitList"));

      setCourseFull(fullCourse);
      handleFullCourse(fullCourse);
    }
  };

  useEffect(() => {
    setCost(
      data
        ?.filter((c: Costs) => c.active === "1")
        .map((c: Costs) => {
          c.baseCost = Number(c.baseCost).toFixed(2);
          c.discountBeforeDueDate = Number(c.discountBeforeDueDate).toFixed(2);
          c.discountMember = Number(c.discountMember).toFixed(2);
          return c;
        }) ?? [],
    );

    if (selectedRegistration.length) {
      setSelectedRegistration([
        data?.find((c: Costs) => c.key === selectedRegistration[0]).key,
      ]);
      onChange(registrationSelected, [
        data?.find((c: Costs) => c.key === selectedRegistration[0]),
      ]);
    }
  }, [data, registrationSelected]);

  useEffect(() => {
    handleWaitList(waitList, watch("waitList"));
  }, [watch("waitList")]);

  useEffect(() => {
    handleLicense(watch("license"));
  }, [watch("license")]);

  const cols = [
    {
      title: "Registration Type",
      dataIndex: "registrationType",
      backGround: "#ffeedc",
    },
    {
      title: "Base Price ($)",
      dataIndex: "baseCost",
      backGround: "#f4fbfe",
    },
    {
      title: "Registered Students",
      dataIndex: "registered",
      backGround: "#f9f9f9",
    },
    {
      title: "Max # of students",
      dataIndex: "maxOfStudents",
      backGround: "#f4f4f4",
    },
    {
      title: "Students in waitlist",
      dataIndex: "waitlist",
      backGround: "#f1f1f1",
      datatype: "datetime",
    },
  ];

  return (
    <div>
      <Table
        rowSelection={{
          type: "radio",
          onChange,
          onSelect: handleSelect,
          selectedRowKeys: selectedRegistration,
        }}
        columns={cols || []}
        dataSource={costs}
        rowClassName="custom-table-row"
      />
      {license && (
        <div className="flex justify-center items-baseline gap-10">
          <Text>
            * Would you like to purchase a {license.title} for ${license.value}{" "}
            CAD?
          </Text>
          <CBSelect
            control={control}
            label=""
            className="flex-none w-16"
            name="license"
            options={[
              { value: "0", label: "No" },
              { value: "1", label: "Yes" },
            ]}
            required
          />
        </div>
      )}
      {waitList && (
        <div className="flex justify-center items-baseline gap-10">
          <Text className="text-red-500">
            This course is full. Do you want to be put on the waiting list?
          </Text>
          <CBSelect
            control={control}
            label=""
            className="flex-none w-16"
            name="waitList"
            options={[
              { value: "0", label: "No" },
              { value: "1", label: "Yes" },
            ]}
            required
          />
        </div>
      )}
      {courseFull && (
        <div className="flex justify-center items-baseline gap-10">
          <Text className="text-red-500">
            This course is full for the selected registration type, you can
            choose a different registration type o try with a different course.
          </Text>
        </div>
      )}
      {error && (
        <Text role="alert" className="text-red-500">
          {error}
        </Text>
      )}
    </div>
  );
};

export default RegistrationCostTable;
